<template>
    <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen py-10 px-4">
        <AppHeader />

        <div class="max-w-3xl mx-auto">
            <div class="flex items-center justify-between mb-6">
                <div>
                    <h1 class="text-3xl font-bold">Pagamento do Projeto</h1>
                    <p class="text-gray-600 mt-1">
                        Escolha a forma de pagamento para liberar o início do trabalho.
                    </p>
                </div>

                <button @click="goBack"
                    class="px-4 py-2 rounded-xl bg-white border shadow-sm hover:bg-gray-50 transition">
                    Voltar
                </button>
            </div>

            <!-- LOADING -->
            <div v-if="loading" class="bg-white border border-[#e0dcd6] rounded-2xl p-6 shadow">
                <p class="text-gray-600">Carregando informações do pagamento...</p>
            </div>

            <!-- ERRO -->
            <div v-else-if="errorMsg" class="bg-white border border-red-200 rounded-2xl p-6 shadow">
                <p class="text-red-700 font-semibold">Não foi possível carregar o pagamento.</p>
                <p class="text-gray-600 mt-1">{{ errorMsg }}</p>
                <button @click="fetchContext"
                    class="mt-4 w-full bg-[#b99a52] text-white py-3 rounded-xl font-semibold hover:bg-[#a18446] transition">
                    Tentar novamente
                </button>
            </div>

            <!-- CONTEÚDO -->
            <div v-else-if="context">
                <!-- JÁ PAGO -->
                <div v-if="isPaid" class="bg-white border border-[#e0dcd6] rounded-2xl p-6 shadow">
                    <h2 class="text-xl font-bold text-emerald-700">Pagamento confirmado</h2>
                    <p class="text-gray-600 mt-1">
                        Seu projeto já está liberado. Você pode continuar de onde parou.
                    </p>

                    <router-link :to="{
                        name: 'detalhes',
                        params: { hash: context.project.hash, slug: context.project.slug }
                    }" class="mt-5 inline-flex w-full justify-center bg-[#b99a52] text-white py-3 rounded-xl font-semibold hover:bg-[#a18446] transition">
                        Ir para o projeto
                    </router-link>
                </div>

                <!-- AINDA NÃO PAGO -->
                <template v-else>
                    <!-- RESUMO -->
                    <div class="bg-white border border-[#e0dcd6] rounded-2xl p-6 shadow mb-6 flex flex-col items-center">

                        <router-link :to="{
                            name: 'detalhes',
                            params: { hash: context.project.hash, slug: context.project.slug }
                        }" class="block">
                            <h2 class="text-xl font-bold text-[#152a34] hover:underline cursor-pointer">
                                {{ context.project.title }}
                            </h2>
                        </router-link>

                        <div class="mt-4 flex flex-wrap justify-center gap-3 w-full">
                            <div class="bg-gray-50 p-4 rounded-xl min-w-[200px] text-center">
                                <p class="text-sm text-gray-500">Valor do freelancer</p>
                                <p class="text-lg font-semibold">R$ {{ formatMoney(context.pricing.baseAmount) }}</p>
                            </div>

                            <div class="bg-gray-50 p-4 rounded-xl min-w-[200px] text-center">
                                <p class="text-sm text-gray-500">Total (PIX)</p>
                                <p class="text-lg font-semibold">R$ {{ formatMoney(context.pricing.totalPix) }}</p>
                                <p class="text-xs text-emerald-700 mt-1 font-semibold">imediato</p>
                            </div>
                        </div>
                    </div>

                    <!-- CARD DESTAQUE PIX -->
                    <div class="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 shadow mb-6">
                        <div class="flex items-start justify-between gap-4">
                            <div>
                                <h2 class="text-xl font-bold text-emerald-800">
                                    PIX — pagamento imediato
                                </h2>
                                <p class="text-emerald-800/80 mt-1">
                                    Confirmação em poucos segundos e <strong>sem custos extras</strong>.
                                </p>

                                <ul class="mt-4 space-y-2 text-sm text-emerald-900/80">
                                    <li>• Libera o projeto na hora</li>
                                    <li>• Mais rápido e recomendado</li>
                                    <li>• Evita qualquer atraso no início do trabalho</li>
                                </ul>
                            </div>

                            <div class="shrink-0">
                                <span
                                    class="inline-flex items-center px-3 py-1 rounded-full bg-emerald-600 text-white text-xs font-semibold">
                                    Recomendado
                                </span>
                            </div>
                        </div>

                        <button @click="openPixModal" :disabled="pixModal.loading"
                            class="mt-5 w-full bg-emerald-600 text-white py-3 rounded-xl font-semibold hover:bg-emerald-700 transition disabled:opacity-60 disabled:cursor-not-allowed">
                            {{ pixModal.loading ? "Gerando PIX..." : `Pagar com PIX — R$
                            ${formatMoney(context.pricing.totalPix)}` }}
                        </button>
                    </div>

                    <!-- CARD CARTÃO -->
                    <div class="bg-white border border-[#e0dcd6] rounded-2xl p-6 shadow">
                        <h2 class="text-xl font-bold text-[#152a34]">
                            Cartão de crédito
                        </h2>

                        <p class="text-gray-600 mt-1">
                            No cartão, podem existir <strong>custos adicionais de processamento</strong>.
                            O valor final aparece antes de confirmar.
                        </p>

                        <div class="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
                            <p class="font-semibold">Prazo:</p>
                            <p>
                                Pagamentos no cartão podem levar <strong>até 5 dias</strong> para compensação,
                                dependendo do emissor e regras do processamento.
                            </p>
                        </div>

                        <div class="mt-4 flex items-center justify-between bg-gray-50 p-4 rounded-xl">
                            <p class="text-sm text-gray-600">Total no cartão</p>
                            <p class="text-lg font-bold">R$ {{ formatMoney(context.pricing.totalCard) }}</p>
                        </div>

                        <button @click="startCardCheckout" :disabled="loadingCard" :loading="loadingCard"
                            class="mt-5 w-full bg-[#1a1a1a] text-white py-3 rounded-xl font-semibold hover:bg-black transition">
                            Pagar com Cartão — R$ {{ formatMoney(context.pricing.totalCard) }}
                        </button>
                    </div>

                    <!-- CONFIRMAÇÃO -->
                    <div v-if="method" class="mt-6 bg-white border border-[#e0dcd6] rounded-2xl p-6 shadow">
                        <h3 class="text-lg font-bold">Forma selecionada</h3>
                        <p class="text-gray-600 mt-1">
                            {{ methodLabel }}
                        </p>

                        <button @click="proceed"
                            class="mt-4 w-full bg-[#b99a52] text-white py-3 rounded-xl font-semibold hover:bg-[#a18446] transition">
                            Continuar
                        </button>
                    </div>
                </template>
            </div>
        </div>

        <!-- MODAL PIX -->
        <div v-if="pixModal.open" class="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div class="absolute inset-0 bg-black/50" @click="closePixModal"></div>

            <div class="relative w-full max-w-3xl bg-white rounded-2xl shadow-xl border border-[#e0dcd6] p-6">
                <div class="flex items-start justify-between gap-3">
                    <div>
                        <h3 class="text-xl font-bold text-[#152a34]">
                            {{ pixModal.approved ? 'Pagamento concluído' : 'Pague com PIX' }}
                        </h3>
                        <p class="text-gray-600 mt-1">
                            {{ pixModal.approved
                                ? 'Confirmamos seu pagamento. Você já pode iniciar o projeto.'
                                : 'Abra o app do seu banco e escaneie o QR Code ou use o código copia-e-cola.' }}
                        </p>
                    </div>

                    <button class="px-3 py-2 rounded-xl bg-white border shadow-sm hover:bg-gray-50 transition"
                        @click="closePixModal">
                        Fechar
                    </button>
                </div>

                <div v-if="pixModal.loading" class="mt-6">
                    <p class="text-gray-600">Gerando cobrança PIX...</p>
                </div>

                <div v-else class="mt-6">
                    <!-- aprovado -->
                    <div v-if="pixModal.approved" class="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                        <p class="text-emerald-900 font-semibold">Pagamento aprovado!</p>
                    </div>

                    <!-- aguardando -->
                    <div v-else>
                        <div class="flex items-center justify-between bg-gray-50 p-4 rounded-xl">
                            <p class="text-sm text-gray-600">Total no PIX</p>
                            <p class="text-lg font-bold">R$ {{ formatMoney(context?.pricing?.totalPix) }}</p>
                        </div>

                        <div class="mt-4 flex items-center justify-center bg-gray-50 border rounded-xl p-4">
                            <img v-if="pixModal.imagem_qrcode" :src="pixModal.imagem_qrcode"
                                class="w-36 h-36 object-contain" />
                            <div v-else class="text-sm text-gray-600">
                                QR Code não disponível como imagem. Use o código copia-e-cola abaixo.
                            </div>
                        </div>

                        <div class="mt-4">
                            <p class="text-sm font-semibold text-gray-700">PIX Copia e Cola</p>
                            <textarea class="mt-2 w-full h-20 p-2 rounded-xl border bg-white text-sm" readonly
                                :value="pixModal.qr_code" />
                            <button
                                class="mt-3 w-full bg-[#1a1a1a] text-white py-3 rounded-xl font-semibold hover:bg-black transition"
                                @click="copyPixCode">
                                Copiar código PIX
                            </button>
                        </div>

                        <div class="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
                            <p class="font-semibold">Aguardando confirmação...</p>
                            <p class="mt-1">
                                Vamos verificar automaticamente. Você não precisa recarregar a página.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <AppFooter />
    </div>
</template>

<script setup>
import { computed, ref, onMounted, getCurrentInstance, onBeforeUnmount } from "vue";
import { useRouter, useRoute } from "vue-router";
import AppHeader from "@/components/AppHeader.vue";
import AppFooter from "@/components/AppFooter.vue";
import api from "@/services/api";

const { proxy } = getCurrentInstance();
const router = useRouter();
const route = useRoute();

const loading = ref(false);
const loadingCard = ref(false);
const errorMsg = ref("");
const context = ref(null);

const method = ref(""); // 'pix' | 'card'

const pixModal = ref({
    open: false,
    loading: false,
    approved: false,
    status: "pending",
    qr_code: "",
    imagem_qrcode: "",
    txid: "",
    expires_at: null,
    error: ""
});

let pollTimer = null;

const isPaid = computed(() => {
    const s = context.value?.project?.status;
    return s === "payment_approved" || s === "in_progress";
});

const methodLabel = computed(() => {
    if (method.value === "pix") return "PIX (imediato e sem custos extras)";
    if (method.value === "card") return "Cartão de crédito (sujeito a custos adicionais e prazo de compensação)";
    return "";
});

function goBack() {
    router.back();
}

function selectMethod(m) {
    method.value = m;
}

function formatMoney(val) {
    const n = Number(val || 0);
    return n.toFixed(2).replace(".", ",");
}

async function fetchContext() {
    loading.value = true;
    errorMsg.value = "";
    context.value = null;

    try {
        const { hash, slug } = route.params;

        if (!hash || !slug) {
            throw new Error("Rota inválida. Parâmetros de projeto ausentes.");
        }

        const res = await api.get(`/projects/payment/${hash}/${slug}`);
        context.value = res.data.data;
    } catch (err) {
        console.error(err);
        errorMsg.value =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err.message ||
            "Erro ao carregar o pagamento.";
    } finally {
        loading.value = false;
    }
}

async function openPixModal() {
    // guard: não gera PIX se já está pago
    if (isPaid.value) {
        proxy?.$swal?.fire?.({
            icon: "info",
            title: "Pagamento já confirmado",
            text: "Este projeto já está liberado."
        });
        return;
    }

    pixModal.value.open = true;
    pixModal.value.loading = true;
    pixModal.value.approved = false;
    pixModal.value.status = "pending";
    pixModal.value.qr_code = "";
    pixModal.value.imagem_qrcode = "";
    pixModal.value.txid = "";
    pixModal.value.expires_at = null;
    pixModal.value.error = "";

    stopPolling();

    try {
        const { hash, slug } = route.params;

        const payload = {
            method: "pix",
            amount: context.value?.pricing?.totalPix,
            description: context.value?.project?.title,
        };

        const res = await api.post(`/projects/payment/${hash}/${slug}/pix`, payload);

        const pay = res.data?.data?.payment;

        pixModal.value.qr_code = pay?.qr_code || "";
        pixModal.value.imagem_qrcode = pay?.imagem_qrcode || "";
        pixModal.value.txid = pay?.txid || "";
        pixModal.value.status = pay?.status || "pending";
        pixModal.value.expires_at = pay?.expires_at || null;

        if (pixModal.value.txid) startPolling();
    } catch (err) {
        console.error(err);
        const msg =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err.message ||
            "Erro ao gerar PIX";

        pixModal.value.error = msg;
        pixModal.value.status = "error";

        proxy?.$swal?.fire?.({
            icon: "error",
            title: "Não foi possível gerar o PIX",
            text: msg
        });
    } finally {
        pixModal.value.loading = false;
    }
}

async function startCardCheckout() {
    // guard: não inicia checkout se já está pago
    if (isPaid.value) {
        proxy?.$swal?.fire?.({
            icon: "info",
            title: "Pagamento já confirmado",
            text: "Este projeto já está liberado."
        });
        return;
    }

    try {
        const { hash, slug } = route.params;

        const payload = {
            method: "card",
            amount: context.value?.pricing?.totalCard,
            description: context.value?.project?.title,
            expiresIn: 3600
        };

        loadingCard.value = true;

        const res = await api.post(`/projects/payment/${hash}/${slug}/checkout`, payload);

        const checkoutUrl = res.data?.data?.checkout?.checkout_url;

        if (!checkoutUrl) {
            throw new Error("Checkout URL não retornou do backend.");
        }

        proxy?.$swal?.fire?.({
            icon: "info",
            title: "Redirecionando…",
            text: "Você será levado para o checkout do cartão.",
            timer: 1200,
            showConfirmButton: false
        });

        window.location.href = checkoutUrl;
    } catch (err) {
        const msg =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err.message ||
            "Erro ao iniciar pagamento no cartão";

        proxy?.$swal?.fire?.({
            icon: "error",
            title: "Falha no cartão",
            text: msg
        });
    } finally {
        loadingCard.value = false;
    }
}

function closePixModal() {
    pixModal.value.open = false;
    stopPolling();
}

function startPolling() {
    stopPolling();
    pollTimer = setInterval(checkPaymentStatus, 3000);
    // roda uma vez já no começo
    checkPaymentStatus();
}

function stopPolling() {
    if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
    }
}

async function checkPaymentStatus() {
    try {
        const { hash, slug } = route.params;
        const res = await api.get(`/payment/status/${hash}/${slug}`);

        const pay = res.data?.data?.payment;
        const proj = res.data?.data?.project;

        const status = pay?.status || "pending";
        pixModal.value.status = status;

        if (status === "approved" || proj?.status === "payment_approved") {
            pixModal.value.approved = true;
            stopPolling();
            // recarrega contexto pra atualizar UI (vai esconder PIX/Cartão via isPaid)
            await fetchContext();
        }
    } catch (err) {
        // não fecha modal por falha de rede; só mantém tentando
        console.warn("Falha polling status:", err?.message || err);
    }
}

async function copyPixCode() {
    try {
        await navigator.clipboard.writeText(pixModal.value.qr_code || "");
        proxy?.$swal?.fire?.({
            icon: "success",
            title: "Copiado!",
            text: "Código PIX copiado para a área de transferência.",
            timer: 1500,
            showConfirmButton: false,
        });
    } catch (e) {
        alert("Não foi possível copiar automaticamente. Selecione e copie manualmente.");
    }
}

function proceed() {
    if (!method.value) return;

    const total =
        method.value === "pix"
            ? context.value?.pricing?.totalPix
            : context.value?.pricing?.totalCard;

    proxy?.$swal?.fire?.({
        icon: "info",
        title: "Próxima etapa",
        text: `Você escolheu ${method.value.toUpperCase()} — Total: R$ ${formatMoney(total)}. Agora vamos gerar a cobrança.`,
    }) || alert(`Método: ${method.value} | Total: R$ ${formatMoney(total)}`);
}

onBeforeUnmount(() => stopPolling());

onMounted(fetchContext);
</script>